import React, { useState, useCallback, useRef, useEffect } from "react";
import { Button } from "@/components/_components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/_components/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/_components/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/_components/table";
import { AGENT_TYPE, TRANSACTION_TYPES, TRANSACTION_STATUS, PAGINATION } from "@/constants/constants";
import { useExportFailureTransactionsCSV } from "@/lib/api/hooks";
import { Search, FileText, Download, RotateCcw, BarChart3, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Play } from "lucide-react";
import { usePaginatedQuery } from "@/hooks/usePaginatedQuery";
import * as api from "@/lib/api/client";
import toast from "react-hot-toast";
import FailureTransactionPayloadDialog from "@/components/failure-transaction-payload-dialog";
import { formatDateTime } from "@/lib/utils";
import { usePageHeader } from "@/contexts/page-header-context";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/_components/badge";
import { PageAccessGuard } from "@/components/auth/PageAccessGuard";
import { useRetryFailureTransaction } from "@/lib/api/hooks";

interface FailureTransactionData {
  id: number;
  transType: string;
  correlationId: string;
  failureReason: string;
  retryCount: number;
  status: string;
  lastRetryAt: string;
  nextRetryAt: string;
  createdAt: string;
}

interface FailureTransactionFilter {
  agentType: string;
  transType?: string;
  correlationId?: string;
  fromDate?: string;
  toDate?: string;
  status?: string[];
}

const FailureTransactionsPage: React.FC = () => {
  return (
    <PageAccessGuard authority="ANALYTICS_FAILURE_TRANSACTIONS_VIEW">
      <FailureTransactionsContent />
    </PageAccessGuard>
  );
};

const FailureTransactionsContent: React.FC = () => {
  const { setHeaderData } = usePageHeader();
  // Helper function to get current date with start and end times
  const getCurrentDateRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');

    const startTime = `${year}-${month}-${day}T00:00`;
    const endTime = `${year}-${month}-${day}T23:59`;

    return { startTime, endTime };
  };

  const { startTime, endTime } = getCurrentDateRange();

  const [formData, setFormData] = useState({
    agentType: "",
    transType: "",
    correlationId: "",
    fromDate: startTime,
    toDate: endTime,
    status: [] as string[],
  });

  const [payloadDialogOpen, setPayloadDialogOpen] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<number>(0);
  const [selectedCorrelationId, setSelectedCorrelationId] = useState<string>("");

  // Use paginated query hook
  const paginatedQuery = usePaginatedQuery<FailureTransactionFilter, FailureTransactionData[]>(
    (filter, options) => api.getFailureTransactions(filter, options),
    {
      pageSize: PAGINATION.DEFAULT_PAGE_SIZE,
    }
  );

  const [selectedPageSize, setSelectedPageSize] = useState(PAGINATION.DEFAULT_PAGE_SIZE);

  const {
    data: transactionData,
    isPending,
    executeQuery,
    currentPage,
    totalCount,
    setCurrentPage,
    currentFilter,
  } = paginatedQuery;

  const pageSize = selectedPageSize;

  const { mutate: exportCSV, isPending: isExporting } = useExportFailureTransactionsCSV({
    mutation: {
      onSuccess: (response) => {
        // Create a blob URL and trigger download
        const blob = new Blob([response.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `failure-transactions-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Failure transactions CSV downloaded successfully");
      },
      onError: (error: any) => {
        console.error("Error exporting CSV:", error);
        toast.error("Failed to export CSV");
      },
    },
  });

  const { mutate: retryTransaction, isPending: isRetrying } = useRetryFailureTransaction({
    mutation: {
      onSuccess: (response) => {
        toast.success(response.data?.message || "Transaction retry initiated successfully");
        // Refresh the data after successful retry
        handleSearch();
      },
      onError: (error: any) => {
        console.error("Error retrying transaction:", error);
        const errorMessage = error?.response?.data?.message || "Failed to retry transaction";
        toast.error(errorMessage);
      },
    },
  });

  const handleInputChange = (field: string, value: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    if (!formData.agentType) {
      toast.error("Agent Type is mandatory");
      return false;
    }

    // Either dates or correlation ID must be provided
    const hasDates = formData.fromDate && formData.toDate;
    const hasCorrelationId = formData.correlationId.trim();

    if (!hasDates && !hasCorrelationId) {
      toast.error("Either From/To Date or Correlation ID is mandatory");
      return false;
    }

    return true;
  };

  // Helper function to format datetime with timezone
  const formatDateTimeWithTimezone = (dateTimeString: string) => {
    if (!dateTimeString) return undefined;

    // Create a date object from the input (YYYY-MM-DDTHH:MM format)
    const date = new Date(dateTimeString);

    // Get timezone offset in minutes and convert to hours
    const timezoneOffset = date.getTimezoneOffset();
    const offsetHours = Math.floor(Math.abs(timezoneOffset) / 60);
    const offsetMinutes = Math.abs(timezoneOffset) % 60;
    const offsetSign = timezoneOffset <= 0 ? '+' : '-';

    // Format as ISO string with timezone
    const timezoneString = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;

    // Return the datetime with seconds and timezone
    return `${dateTimeString}:00${timezoneString}`;
  };

  const handleSearch = useCallback(() => {
    if (!validateForm()) return;

    const filter: FailureTransactionFilter = {
      agentType: formData.agentType,
      ...(formData.transType && { transType: formData.transType }),
      ...(formData.correlationId.trim() && { correlationId: formData.correlationId.trim() }),
      ...(formData.fromDate && { fromDate: formatDateTimeWithTimezone(formData.fromDate) }),
      ...(formData.toDate && { toDate: formatDateTimeWithTimezone(formData.toDate) }),
      ...(formData.status.length > 0 && { status: formData.status }),
    };

    executeQuery(filter, 1); // Reset to first page on new search
  }, [formData, executeQuery]);

  const handleStatusChange = (value: string) => {
    const currentStatus = formData.status;
    if (currentStatus.includes(value)) {
      handleInputChange("status", currentStatus.filter(s => s !== value));
    } else {
      handleInputChange("status", [...currentStatus, value]);
    }
  };

  const handleShowPayload = (id: number, correlationId: string) => {
    setSelectedTransactionId(id);
    setSelectedCorrelationId(correlationId);
    setPayloadDialogOpen(true);
  };

  const handleRetryTransaction = (id: number) => {
    retryTransaction(id);
  };

  const handleDownloadCSV = () => {
    if (!validateForm()) {
      toast.error("Please ensure search criteria are valid before downloading");
      return;
    }

    const exportParams = {
      agentType: formData.agentType,
      ...(formData.transType && { transType: formData.transType }),
      ...(formData.correlationId.trim() && { correlationId: formData.correlationId.trim() }),
      ...(formData.fromDate && { fromDate: formatDateTimeWithTimezone(formData.fromDate) }),
      ...(formData.toDate && { toDate: formatDateTimeWithTimezone(formData.toDate) }),
      ...(formData.status.length > 0 && { status: formData.status }),
    };

    exportCSV(exportParams);
  };

  const handleReset = () => {
    const { startTime, endTime } = getCurrentDateRange();
    setFormData({
      agentType: "",
      transType: "",
      correlationId: "",
      fromDate: startTime,
      toDate: endTime,
      status: [],
    });
    toast.success("Form reset successfully");
  };

  // Use a ref to track if we've already loaded initial data
  const initialLoadRef = useRef(false);

  // Initial load - don't auto-search, wait for user to search
  useEffect(() => {
    if (initialLoadRef.current) {
      return;
    }
    initialLoadRef.current = true;

    // Cleanup function to reset the ref if component unmounts
    return () => {
      initialLoadRef.current = false;
    };
  }, []);

  // Set page header data - only stats, no title
  useEffect(() => {
    setHeaderData({
      stats: transactionData && Array.isArray(transactionData) ? {
        count: totalCount,
        label: 'record',
        icon: <BarChart3 className="h-4 w-4" />
      } : undefined,
    });

    // Cleanup: clear header when component unmounts
    return () => {
      setHeaderData(null);
    };
  }, [transactionData, totalCount, setHeaderData]);

  return (
    <div className="bg-background">
      {/* Main Content */}
      <div className="w-full mx-auto px-2 sm:px-2 lg:px-2 py-2">
        <div className="space-y-4">
          {/* Search & Filter Section - Modern Clean Design */}
          <div className="bg-card border border-border/50 rounded-lg mb-8">
            {/* Compact Header */}
            <div className="px-4 pt-3 pb-4 border-b border-border/50">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 bg-primary/10 rounded-md">
                  <Search className="h-4 w-4 text-primary" />
                </div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-foreground leading-tight">
                    Search & Filter
                  </h3>
                  <span className="text-sm text-muted-foreground">•</span>
                  <p className="text-sm text-muted-foreground">
                    Find failure transactions by type, date, or correlation ID
                  </p>
                </div>
              </div>
            </div>

            {/* Search & Filter Controls */}
            <div className="px-4 pb-4">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Agent Type */}
                  <div className="space-y-1.5">
                    <label htmlFor="agentType" className="block text-sm font-semibold text-foreground">
                      Agent Type <span className="text-destructive text-base font-bold">*</span>
                    </label>
                    <Select
                      value={formData.agentType}
                      onValueChange={(value) => handleInputChange("agentType", value)}
                    >
                      <SelectTrigger className="h-9 bg-background border border-border hover:border-primary/60 transition-colors focus:ring-2 focus:ring-primary/20">
                        <SelectValue placeholder="Select agent type" />
                      </SelectTrigger>
                      <SelectContent>
                        {AGENT_TYPE.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Transaction Type */}
                  <div className="space-y-1.5">
                    <label htmlFor="transType" className="block text-sm font-semibold text-foreground">
                      Transaction Type
                    </label>
                    <Select
                      value={formData.transType}
                      onValueChange={(value) => handleInputChange("transType", value)}
                    >
                      <SelectTrigger className="h-9 bg-background border border-border hover:border-primary/60 transition-colors focus:ring-2 focus:ring-primary/20">
                        <SelectValue placeholder="Select transaction type" />
                      </SelectTrigger>
                      <SelectContent>
                        {TRANSACTION_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Correlation ID */}
                  <div className="space-y-1.5">
                    <label htmlFor="correlationId" className="block text-sm font-semibold text-foreground">
                      Correlation ID <span className="text-destructive text-base font-bold">*</span>
                    </label>
                    <Input
                      id="correlationId"
                      type="text"
                      placeholder="Enter correlation ID"
                      value={formData.correlationId}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange("correlationId", e.target.value)}
                      className="h-9 bg-background border border-border hover:border-primary/60 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                    />
                  </div>

                  {/* From Date and Time */}
                  <div className="space-y-1.5">
                    <label htmlFor="fromDate" className="block text-sm font-semibold text-foreground">
                      From Date & Time <span className="text-destructive">*</span>
                    </label>
                    <input
                      id="fromDate"
                      type="datetime-local"
                      className="h-9 w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 hover:border-primary/60"
                      value={formData.fromDate}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange("fromDate", e.target.value)}
                    />
                  </div>

                  {/* To Date and Time */}
                  <div className="space-y-1.5">
                    <label htmlFor="toDate" className="block text-sm font-semibold text-foreground">
                      To Date & Time <span className="text-destructive text-base font-bold">*</span>
                    </label>
                    <input
                      id="toDate"
                      type="datetime-local"
                      className="h-9 w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 hover:border-primary/60"
                      value={formData.toDate}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange("toDate", e.target.value)}
                    />
                  </div>

                  {/* Status */}
                  <div className="space-y-1.5">
                    <label className="block text-sm font-semibold text-foreground">
                      Status
                    </label>
                    <div className="space-y-2 p-2 border border-border rounded-lg bg-background">
                      {TRANSACTION_STATUS.map((status) => (
                        <label key={status} className="flex items-center space-x-2 cursor-pointer hover:bg-muted/30 p-1 rounded transition-colors">
                          <input
                            type="checkbox"
                            checked={formData.status.includes(status)}
                            onChange={() => handleStatusChange(status)}
                            className="rounded border-border text-primary focus:ring-primary/20 focus:ring-2"
                          />
                          <span className="text-sm text-foreground">{status}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Action Buttons and Help Text Section */}
                <div className="flex flex-col items-end gap-3 pt-3 border-t border-border/50">
                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={handleSearch}
                      disabled={isPending}
                      className="h-9 px-4 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-200 font-semibold text-sm rounded-md"
                    >
                      {isPending ? (
                        <>
                          <Search className="h-4 w-4 mr-1.5 animate-pulse" />
                          Searching...
                        </>
                      ) : (
                        <>
                          <Search className="h-4 w-4 mr-1.5" />
                          Search
                        </>
                      )}
                    </Button>

                    <Button
                      onClick={handleDownloadCSV}
                      disabled={isExporting}
                      className="h-9 px-4 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-200 font-semibold text-sm rounded-md"
                    >
                      <Download className="h-4 w-4 mr-1.5" />
                      {isExporting ? "Exporting..." : "Download CSV"}
                    </Button>

                    <Button
                      onClick={handleReset}
                      className="h-9 px-4 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-200 font-semibold text-sm rounded-md"
                    >
                      <RotateCcw className="h-4 w-4 mr-1.5" />
                      Reset
                    </Button>
                  </div>

                  {/* Help Text - Compact Right Aligned */}
                  <div className="text-xs text-muted-foreground max-w-2xl text-right leading-relaxed">
                    <p className="mb-1">
                      <span className="text-destructive font-semibold">*</span> Agent Type is mandatory • Either From/To Date & Time or Correlation ID is mandatory
                    </p>
                    <p className="text-muted-foreground/80">
                      All other fields are optional filters
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Results Table */}
          {transactionData && (
            <>
              {Array.isArray(transactionData) && transactionData.length === 0 ? (
                <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-12 text-center">
                    <div className="max-w-md mx-auto">
                      <div className="w-16 h-16 mx-auto mb-4 bg-muted/30 rounded-full flex items-center justify-center">
                        <Search className="w-8 h-8 text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">No Transactions Found</h3>
                      <p className="text-muted-foreground mb-4">
                        We couldn't find any failure transactions matching your search criteria.
                      </p>
                      <Button
                        variant="outline"
                        onClick={handleReset}
                        className="text-primary border-primary hover:bg-primary/10 shadow-sm hover:shadow-md transition-all duration-200"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Clear Filters
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg font-semibold text-foreground">
                        Failure Transaction Results <span className="text-primary font-bold">{totalCount}</span> {totalCount === 1 ? 'record' : 'records'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="hover:bg-transparent">
                              <TableHead className="font-semibold">Sl No</TableHead>
                              <TableHead className="font-semibold">Transaction Type</TableHead>
                              <TableHead className="font-semibold">Correlation ID</TableHead>
                              <TableHead className="font-semibold">Failure Reason</TableHead>
                              <TableHead className="font-semibold">Retry Count</TableHead>
                              <TableHead className="font-semibold">Status</TableHead>
                              <TableHead className="font-semibold">Last Retry At</TableHead>
                              <TableHead className="font-semibold">Next Retry At</TableHead>
                              <TableHead className="font-semibold">Created At</TableHead>
                              <TableHead className="font-semibold text-center">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {transactionData?.map((row, index) => {
                              // Calculate serial number based on current page and index
                              const serialNumber = (currentPage - 1) * pageSize + index + 1;
                              return (
                                <TableRow
                                  key={index}
                                  className="transition-colors hover:bg-muted/50 cursor-default"
                                >
                                  <TableCell className="font-medium">{serialNumber}</TableCell>
                                  <TableCell>{row.transType || '-'}</TableCell>
                                  <TableCell className="font-mono text-xs">{row.correlationId || '-'}</TableCell>
                                  <TableCell className="max-w-md break-words whitespace-pre-wrap">
                                    {row.failureReason || '-'}
                                  </TableCell>
                                  <TableCell>{row.retryCount}</TableCell>
                                  <TableCell>
                                    {(() => {
                                      const getStatusBadge = (status: string) => {
                                        const normalizedStatus = status?.toUpperCase() || '';
                                        if (normalizedStatus === 'FAILED_FINAL') {
                                          return <Badge variant="destructive">{status}</Badge>;
                                        } else if (normalizedStatus === 'PENDING') {
                                          return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20 dark:bg-yellow-500/20 dark:text-yellow-400">{status}</Badge>;
                                        } else if (normalizedStatus === 'PROCESSING') {
                                          return <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20 dark:bg-blue-500/20 dark:text-blue-400">{status}</Badge>;
                                        } else if (normalizedStatus === 'COMPLETED') {
                                          return <Badge variant="default" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:bg-emerald-500/20 dark:text-emerald-400">{status}</Badge>;
                                        }
                                        return <Badge variant="secondary">{status}</Badge>;
                                      };
                                      return getStatusBadge(row.status);
                                    })()}
                                  </TableCell>
                                  <TableCell className="text-sm">{formatDateTime(row.lastRetryAt)}</TableCell>
                                  <TableCell className="text-sm">{formatDateTime(row.nextRetryAt)}</TableCell>
                                  <TableCell className="text-sm">{formatDateTime(row.createdAt)}</TableCell>
                                  <TableCell className="text-center">
                                    <div className="flex items-center justify-center gap-1">
                                      {(row.status?.toUpperCase() === 'PENDING' || row.status?.toUpperCase() === 'FAILED_FINAL') && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleRetryTransaction(row.id)}
                                          disabled={isRetrying}
                                          className="h-8 w-8 p-0 hover:bg-green-500/10 hover:text-green-600 transition-colors"
                                          title="Retry Transaction"
                                        >
                                          <Play className="h-4 w-4" />
                                        </Button>
                                      )}
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleShowPayload(row.id, row.correlationId)}
                                        className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary transition-colors"
                                        title="View Payload"
                                      >
                                        <FileText className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                    {/* Pagination matching dashboard style */}
                    <div className="flex items-center justify-between px-4 mt-4">
                      <div className="hidden flex-1 text-sm text-muted-foreground lg:flex">
                        {totalCount} {totalCount === 1 ? 'record' : 'records'}
                      </div>
                      <div className="flex w-full items-center gap-8 lg:w-fit">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">Rows per page</span>
                          <Select
                            value={pageSize.toString()}
                            onValueChange={(value) => {
                              const newSize = Number(value);
                              setSelectedPageSize(newSize);
                              if (currentFilter) {
                                executeQuery(currentFilter, 1, newSize);
                              } else {
                                setCurrentPage(1);
                              }
                            }}
                            disabled={isPending}
                          >
                            <SelectTrigger className="h-8 w-[70px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {PAGINATION.PAGE_SIZE_OPTIONS.map(size => (
                                <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground whitespace-nowrap">
                            Page {currentPage} of {Math.ceil(totalCount / pageSize) || 1}
                          </span>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => setCurrentPage(1)}
                              disabled={currentPage === 1 || isPending}
                              title="First page"
                            >
                              <ChevronsLeft className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => setCurrentPage(currentPage - 1)}
                              disabled={currentPage === 1 || isPending}
                              title="Previous page"
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => setCurrentPage(currentPage + 1)}
                              disabled={currentPage >= Math.ceil(totalCount / pageSize) || isPending}
                              title="Next page"
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => setCurrentPage(Math.ceil(totalCount / pageSize))}
                              disabled={currentPage >= Math.ceil(totalCount / pageSize) || isPending}
                              title="Last page"
                            >
                              <ChevronsRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Payload Dialog */}
      <FailureTransactionPayloadDialog
        isOpen={payloadDialogOpen}
        onClose={() => setPayloadDialogOpen(false)}
        transactionId={selectedTransactionId}
        correlationId={selectedCorrelationId}
      />
    </div>
  );
};

export default FailureTransactionsPage;